/* eslint-disable max-len */
import React, {
  useCallback, useEffect, useState,
} from 'react';
import { ArrowUp, Search as SearchIcon } from 'lucide-react';
import { IBackgroundImages } from '@/api/home/types';
import { sendMessageToAiAssistant } from '@/util/sendMessageToAiAssistant';
import { SearchPopup } from '@/components/home/SearchPopup';
import { SkiGptLogo } from '@/components/common/SkiGptLogo';
import { useAnalytics } from '@/tracking/analytics';
import { setFocusToSearchInput } from '@/util/setFocusToInputByQuery';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';

// AI ski-concierge hero, EN_US / EN_GB only. Single SkiGPT input — no
// mode toggle. A solid-white "SKIGPT · AI ski concierge" sticker chip
// half-overlaps the top of the card so the brand reads cleanly on any
// hero photo. The card itself is a chat-style textarea with the
// recommendation pills on the bottom-left and a ChatGPT-style up-arrow
// submit on the bottom-right. Below the card, a subdued dark pill
// opens the resort/region SearchPopup ("or Search over 2,000 ski
// resorts and regions") on both desktop and mobile. On mobile we hide
// the chip's AI-concierge tail so the hero stays focused on the input.

const TYPE_MS = 55;
const DELETE_MS = 25;
const HOLD_MS = 1800;

const useTypewriter = (prompts: string[]): string => {
  const [text, setText] = useState(prompts[0] ?? '');

  useEffect(() => {
    if (typeof window === 'undefined' || prompts.length === 0) return undefined;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce || prompts.length === 1) {
      setText(prompts[0]);
      return undefined;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;
    let promptIndex = 0;
    let charIndex = prompts[0].length;
    let phase: 'hold' | 'delete' | 'type' = 'hold';

    const step = () => {
      if (phase === 'hold') {
        phase = 'delete';
        timer = setTimeout(step, HOLD_MS);
        return;
      }
      if (phase === 'delete') {
        charIndex = Math.max(0, charIndex - 1);
        setText(prompts[promptIndex].slice(0, charIndex));
        if (charIndex === 0) {
          promptIndex = (promptIndex + 1) % prompts.length;
          phase = 'type';
        }
        timer = setTimeout(step, DELETE_MS);
        return;
      }
      const next = prompts[promptIndex];
      charIndex = Math.min(next.length, charIndex + 1);
      setText(next.slice(0, charIndex));
      if (charIndex === next.length) {
        phase = 'hold';
      }
      timer = setTimeout(step, TYPE_MS);
    };

    timer = setTimeout(step, HOLD_MS);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [prompts]);

  return text;
};

interface Props {
  backgroundImage: IBackgroundImages;
  suggestions?: string[];
  searchQuery?: string | string[] | undefined;
  clearSearchQuery?: () => void;
}

export const AiHomeHero = ({
  backgroundImage,
  suggestions,
  searchQuery,
  clearSearchQuery,
}: Props) => {
  const { t } = useOTSTranslation('common');
  const a = useAnalytics();

  const DEFAULT_SUGGESTIONS = [
    t('home.aiHero.suggestion1'),
    t('home.aiHero.suggestion2'),
  ];

  // Placeholder rotates between these prompts with a typewriter animation
  // while the textarea is empty. Slows skim-readers down enough to notice
  // the chat affordance is open-ended without a separate "we are AI now"
  // callout. Skipped when prefers-reduced-motion is set.
  const PLACEHOLDER_PROMPTS = [
    t('home.aiHero.placeholder1'),
    t('home.aiHero.placeholder2'),
    t('home.aiHero.placeholder3'),
    t('home.aiHero.placeholder4'),
  ];

  const [inputValue, setInputValue] = useState('');
  const [isSearchOpen, setSearchOpen] = useState<boolean>(false);
  const placeholder = useTypewriter(PLACEHOLDER_PROMPTS);

  const pills = (suggestions && suggestions.length > 0)
    ? suggestions.slice(0, 2)
    : DEFAULT_SUGGESTIONS;

  const handleSend = () => {
    if (inputValue.trim()) {
      sendMessageToAiAssistant(inputValue);
      setInputValue('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleSearch = useCallback(() => {
    new Promise<void>((resolve) => {
      if (clearSearchQuery) {
        clearSearchQuery();
      }
      setSearchOpen((open) => !open);
      resolve();
    }).then(() => {
      setFocusToSearchInput();
    }).catch(() => {});
  }, [clearSearchQuery]);

  const openSearch = useCallback(() => {
    if (!isSearchOpen) {
      a.searchBox();
    }
    toggleSearch();
  }, [isSearchOpen, a, toggleSearch]);

  useEffect(() => {
    setSearchOpen(!!searchQuery);
  }, [searchQuery]);

  return (
    // z-[11] keeps the hero (and its search card) above the Top Ad's stacking
    // context, while staying below the HeaderNav (z-[12]). Mirrors HomeHero.
    <header className="relative z-[11] overflow-hidden">
      <img
        src={backgroundImage.image}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-center"
      />
      <div
        className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"
        aria-hidden
      />

      {isSearchOpen && (
        <SearchPopup
          closePopup={toggleSearch}
          query={searchQuery}
        />
      )}

      <div className="relative flex min-h-[90vh] items-center px-4 py-14 md:py-20 lg:py-24">
        <div className="mx-auto w-full max-w-2xl text-center text-white">
          {/* Decorative brand line, not the H1. The page's H1 is the
              keyword-bearing heading in the content area below (see
              page-components/global/home.tsx) — the same split production
              uses, where the hero is a styled span rather than a heading. */}
          <p className="mb-12 font-heading text-4xl font-bold tracking-tight text-white md:mb-14 md:text-5xl lg:text-6xl">
            {t('home.aiHero.title')}
          </p>

          <div className="relative">
            {/* py-1.5 + a 20px lockup keeps the chip at 32px tall, so -top-4
                still centres it on the card's top edge. The 20px lockup also
                matches the text-sm line-height of the concierge tail. */}
            <div className="absolute -top-4 left-1/2 z-10 inline-flex -translate-x-1/2 items-center gap-2 rounded-full bg-white px-4 py-1.5 text-sm shadow-md ring-1 ring-black/5">
              <SkiGptLogo className="h-5" />
              <span className="text-muted-foreground max-md:hidden">{`· ${t('home.aiHero.conciergeLabel')}`}</span>
            </div>

            <form
              role="search"
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
              className="rounded-2xl bg-white p-3 pt-5 text-left shadow-2xl ring-1 ring-black/5"
            >
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={2}
                maxLength={450}
                placeholder={placeholder}
                className="block w-full resize-none border-0 bg-transparent px-2 py-2 text-base leading-relaxed text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-0"
              />

              <div className="mt-2 flex items-end justify-between gap-3 px-1">
                <div className="flex min-w-0 flex-wrap gap-1.5">
                  {pills.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => sendMessageToAiAssistant(s)}
                      className="whitespace-nowrap rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground/80 transition hover:border-primary/40 hover:bg-sky-50 hover:text-secondary"
                    >
                      {s}
                    </button>
                  ))}
                </div>

                <button
                  type="submit"
                  aria-label={t('home.aiHero.askSkiGpt')}
                  disabled={!inputValue.trim()}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-white transition hover:bg-secondary/90 disabled:bg-secondary/40"
                >
                  <ArrowUp size={16} className="fill-none" strokeWidth={2.5} />
                </button>
              </div>
            </form>
          </div>

          <div className="mt-5 flex items-center justify-center">
            <button
              type="button"
              onClick={openSearch}
              className="group inline-flex items-center gap-2 rounded-full bg-black/45 px-4 py-2 text-sm text-white shadow-sm ring-1 ring-white/15 backdrop-blur transition hover:bg-black/60"
            >
              <SearchIcon size={14} className="fill-none" />
              <span className="font-semibold underline-offset-2 group-hover:underline">
                {t('home.aiHero.searchCta')}
              </span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};
